import { createColumnHelper } from '@tanstack/react-table';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { UserAvatar } from '@/shared/components/custom/avatars';
import { getUserColor } from '@/core/lib/colors';
import { cn } from '@/core/lib/utils';
import { getEfficiencyLevel, LEVEL_STYLES } from '../components/PerformanceCellBadge';
import { formatCurrency, formatMinutes } from '../utils';
import type { MemberTableEntry } from '../types';
import type { TFunction } from 'i18next';

const helper = createColumnHelper<MemberTableEntry>();

export function getPerformanceTableColumns(t: TFunction) {
  return [
    helper.accessor('memberName', {
      header: t('table.collaboratorCol'),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <UserAvatar
            name={row.original.memberName}
            color={getUserColor(row.original.memberId)}
            src={row.original.memberAvatarUrl ?? undefined}
            size="sm"
          />
          <div className="flex flex-col min-w-0">
            <span className="truncate text-sm font-medium">{row.original.memberName}</span>
            <span className="text-xs text-muted-foreground">
              {t('table.hourlyRate', { rate: row.original.hourlyRate })}
            </span>
          </div>
        </div>
      ),
    }),
    helper.accessor('totalTasks', {
      header: t('table.tasksCol'),
      cell: ({ getValue }) => <span className="text-sm">{getValue()}</span>,
    }),
    helper.accessor('estimatedMinutes', {
      header: t('table.estHoursCol'),
      cell: ({ getValue }) => <span className="text-sm">{formatMinutes(getValue())}</span>,
    }),
    helper.accessor('realMinutes', {
      header: t('table.realHoursCol'),
      cell: ({ getValue }) => <span className="text-sm">{formatMinutes(getValue())}</span>,
    }),
    helper.accessor('estimatedCost', {
      header: t('table.estCostCol'),
      cell: ({ getValue }) => <span className="text-sm">{formatCurrency(getValue())}</span>,
    }),
    helper.accessor('realCost', {
      header: t('table.realCostCol'),
      cell: ({ getValue }) => <span className="text-sm">{formatCurrency(getValue())}</span>,
    }),
    helper.accessor('gainLoss', {
      header: t('table.gainLossCol'),
      cell: ({ getValue }) => {
        const value = getValue();
        const isPositive = value > 0;
        const isNegative = value < 0;
        return (
          <span
            className={cn(
              'flex items-center gap-1 text-sm font-medium',
              isPositive && 'text-green-600',
              isNegative && 'text-red-600',
              !isPositive && !isNegative && 'text-muted-foreground',
            )}
          >
            {isPositive && <TrendingUp className="h-3.5 w-3.5" />}
            {isNegative && <TrendingDown className="h-3.5 w-3.5" />}
            {isPositive ? '+' : ''}
            {formatCurrency(value)}
          </span>
        );
      },
    }),
    helper.accessor('efficiency', {
      header: t('table.efficiencyCol'),
      cell: ({ getValue }) => {
        const eff = getValue();
        const level = getEfficiencyLevel(eff);
        const pct = Math.min(eff, 100);
        return (
          <div className="flex min-w-[120px] items-center gap-2">
            <div className="flex-1 rounded-full bg-muted h-1.5 overflow-hidden">
              <div className="h-full rounded-full bg-green-500" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-10 text-right text-sm font-medium">{eff}%</span>
            <span
              className={cn('rounded px-1.5 py-0.5 text-[10px] font-semibold', LEVEL_STYLES[level])}
            >
              {t(`kpi.level.${level}`)}
            </span>
          </div>
        );
      },
    }),
  ];
}
